"use client";
import { useState, type MouseEvent } from "react";
import { useNavigate } from "react-router-dom";
import PageContainer from "@/app/admin/_common/PageContainer";
import CuoButton from "@/app/admin/_common/CuoButton";
import CuoEmpty from "@/app/admin/_common/CuoEmpty";
import { Tabs, TabsList, TabsTrigger } from "@/app/admin/_common/ui/tabs";
import { Badge } from "@/app/admin/_common/ui/badge";
import { Sheet, SheetContent, SheetTrigger } from "@/app/admin/_common/ui/sheet";
import {
  PartyPopper,
  ArrowLeft,
  Eye,
  EyeOff,
  Drama,
  Activity,
  ExternalLink,
  MoreVertical,
} from "lucide-react";
import { absolutePublicFestivalUrl } from "@/app/_utils/publicProductUrl";
import { cn } from "@/app/_utils/cn";
import FestivalService from "@/app/_services/FestivalService";
import { useFestivalDetailModel } from "./festivalDetailPageModel";
import DetailStatTiles from "@/app/admin/_common/DetailStatTiles";
import FestivalSalesCard from "./_cmp/FestivalSalesCard";
import FestivalRecentOrdersCard from "./_cmp/FestivalRecentOrdersCard";
import FestivalShowsList from "./_cmp/FestivalShowsList";
import FestivalDetailSkeleton from "./_cmp/FestivalDetailSkeleton";
import FestivalGuestsSection from "./_cmp/guests/FestivalGuestsSection";
import FestivalDetailsTab from "./_cmp/FestivalDetailsTab";
import FestivalDetailsTabSkeleton from "./_cmp/FestivalDetailsTabSkeleton";
import FestivalOrdersTabSkeleton from "./_cmp/FestivalOrdersTabSkeleton";
import { toast } from "@/app/_utils/toast";

type FestivalTab = "overview" | "details" | "activity" | "orders";

const tabTriggerClassName =
  "rounded-none border-b-2 border-transparent bg-transparent px-0 pb-2.5 text-muted-foreground shadow-none data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none";

const currencyFormat = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});
const numberFormat = new Intl.NumberFormat("en-US");

function festivalKpiTiles(kpis: import("@/app/_services/FestivalService").FestivalKpis) {
  return [
    { label: "Total revenue", value: currencyFormat.format(kpis.revenue), caption: "All time" },
    { label: "Avg capacity", value: numberFormat.format(Math.round(kpis.avg_capacity)), caption: "Per show" },
    { label: "Tickets available", value: numberFormat.format(kpis.tickets_available), caption: "All time" },
    { label: "Tickets sold", value: numberFormat.format(kpis.tickets_sold), caption: "All time" },
    {
      label: "Avg purchase size",
      value: numberFormat.format(Math.round(kpis.avg_purchase_size * 10) / 10),
      caption: "Tickets per order",
    },
    {
      label: "Average age",
      value: kpis.avg_age != null ? numberFormat.format(Math.round(kpis.avg_age)) : "—",
      caption: "Years",
    },
  ];
}

export default function FestivalDetailPage() {
  const navigate = useNavigate();
  const { data, isLoading, refetch } = useFestivalDetailModel();
  const [activeTab, setActiveTab] = useState<FestivalTab>("overview");
  const [isPublishing, setIsPublishing] = useState(false);

  const festival = data?.festival;
  const showCount = data?.shows.length ?? 0;

  async function handleTogglePublish() {
    if (!festival) return;
    setIsPublishing(true);
    try {
      await FestivalService.publishFestivalAsync(festival.id, !festival.published);
      toast.success(festival.published ? "Festival unpublished" : "Festival published");
      refetch();
    } catch (e) {
      toast.error("Failed to update festival");
    } finally {
      setIsPublishing(false);
    }
  }

  const publicUrl = festival?.slug ? absolutePublicFestivalUrl(festival.slug) : undefined;
  const publicLinkLabel = festival && !festival.published ? "Preview" : "View public page";

  // Unpublished festivals 404 publicly, so open the page with a signed preview token.
  async function handleOpenPublicPage(e: MouseEvent<HTMLAnchorElement>) {
    if (!festival || festival.published || !publicUrl) return;
    e.preventDefault();
    // Open synchronously so the popup blocker treats it as user-initiated.
    const win = window.open("", "_blank");
    try {
      const token = await FestivalService.getPreviewTokenAsync(festival.id);
      if (win) win.location.href = `${publicUrl}?preview=${encodeURIComponent(token)}`;
    } catch {
      win?.close();
      toast.error("Failed to open preview");
    }
  }

  const titleContent = (
    <h1 className="flex min-w-0 items-center gap-2 truncate text-[21px] font-bold tracking-tight text-foreground">
      <PartyPopper className="size-5 flex-none text-foreground" />
      {festival?.title ?? "Festival"}
    </h1>
  );

  const metaContent = (
    <div className="flex flex-wrap items-center gap-3 text-[12.5px] text-muted-foreground">
      <span className="inline-flex items-center gap-1">
        <Drama className="size-3.5" />
        {showCount} {showCount === 1 ? "show" : "shows"}
      </span>
      {festival && (
        <Badge
          variant="outline"
          className={`gap-1.5 rounded-[7px] font-normal ${
            festival.published
              ? "border-emerald-200 bg-emerald-50 text-emerald-700"
              : "border-slate-200 bg-slate-50 text-slate-600"
          }`}
        >
          <span
            className={`size-1.5 flex-none rounded-full ${
              festival.published ? "bg-emerald-500" : "bg-slate-400"
            }`}
          />
          {festival.published ? "Published" : "Unpublished"}
        </Badge>
      )}
    </div>
  );

  const mobileActionsSheet = (
    <Sheet>
      <SheetTrigger asChild>
        <button
          type="button"
          aria-label="Festival actions"
          className="inline-flex size-[30px] flex-none items-center justify-center rounded-lg border border-input bg-card text-foreground transition-colors hover:bg-accent hover:text-accent-foreground md:hidden"
        >
          <MoreVertical className="size-4 shrink-0" size={16} />
        </button>
      </SheetTrigger>
      <SheetContent side="bottom" className="rounded-t-xl bg-white p-0 md:hidden">
        <div className="flex flex-col pb-6 pt-6">
          <a
            href={publicUrl}
            onClick={handleOpenPublicPage}
            target="_blank"
            rel="noopener noreferrer"
            aria-disabled={!festival?.slug}
            className={cn(
              "flex h-11 items-center gap-2.5 px-6 text-sm font-normal text-foreground transition-colors hover:bg-muted",
              !festival?.slug && "pointer-events-none opacity-50"
            )}
          >
            <ExternalLink className="size-3.5" />
            {publicLinkLabel}
          </a>
          <div className="mx-6 border-t border-border" />
          <CuoButton
            variant="ghost"
            label={festival?.published ? "Unpublish" : "Publish"}
            icon={
              festival?.published ? (
                <EyeOff className="size-3.5" />
              ) : (
                <Eye className="size-3.5" />
              )
            }
            onClick={handleTogglePublish}
            disableOnAsync={isPublishing}
            disabled={!festival}
            className="h-11 w-full justify-start gap-2.5 rounded-none px-6 font-normal"
          />
        </div>
      </SheetContent>
    </Sheet>
  );

  return (
    <PageContainer flush>
      <div className="flex h-full w-full flex-col">
        <div className="border-b border-border bg-card/60 px-5 pt-5 backdrop-blur-[2px]">
          <button
            type="button"
            onClick={() => navigate("/admin/festivals")}
            className="mb-0.5 inline-flex items-center gap-1 text-[12.5px] text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="size-3.5" />
            Back to Festivals
          </button>

          {/* Mobile: title + actions trigger on one row, meta on its own row below */}
          <div className="flex flex-col gap-2 md:hidden">
            <div className="flex items-center justify-between gap-3">
              {titleContent}
              {mobileActionsSheet}
            </div>
            {metaContent}
          </div>

          {/* Desktop: title + meta grouped on the left, actions on the right */}
          <div className="hidden items-center justify-between gap-3 md:flex">
            <div className="flex min-w-0 items-center gap-3">
              {titleContent}
              {metaContent}
            </div>
            <div className="flex flex-none items-center gap-2">
              <a
                href={publicUrl}
                onClick={handleOpenPublicPage}
                target="_blank"
                rel="noopener noreferrer"
                aria-disabled={!festival?.slug}
                className={cn(
                  "inline-flex h-[30px] items-center gap-1.5 rounded-lg border border-input bg-card px-3 text-sm font-normal text-foreground transition-colors hover:bg-accent hover:text-accent-foreground",
                  !festival?.slug && "pointer-events-none opacity-50"
                )}
              >
                <ExternalLink className="size-3.5" />
                {publicLinkLabel}
              </a>
              <CuoButton
                variant="outline"
                label={festival?.published ? "Unpublish" : "Publish"}
                icon={
                  festival?.published ? (
                    <EyeOff className="size-3.5" />
                  ) : (
                    <Eye className="size-3.5" />
                  )
                }
                onClick={handleTogglePublish}
                disableOnAsync={isPublishing}
                disabled={!festival}
              />
            </div>
          </div>

          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as FestivalTab)} className="mt-1">
            <TabsList className="h-auto w-full justify-start gap-5 overflow-x-auto border-0 bg-transparent p-0">
              <TabsTrigger value="overview" className={`flex-none ${tabTriggerClassName}`}>
                Overview
              </TabsTrigger>
              <TabsTrigger value="details" className={`flex-none ${tabTriggerClassName}`}>
                Details
              </TabsTrigger>
              <TabsTrigger value="orders" className={`flex-none ${tabTriggerClassName}`}>
                Orders
              </TabsTrigger>
              <TabsTrigger value="activity" className={`flex-none ${tabTriggerClassName}`}>
                Activity
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-5">
          <div className="flex flex-col gap-3">
          {isLoading || !data ? (
            activeTab === "details" ? (
              <FestivalDetailsTabSkeleton />
            ) : activeTab === "orders" ? (
              <FestivalOrdersTabSkeleton />
            ) : activeTab === "activity" ? (
              <div className="flex h-[calc(100vh-320px)] flex-col overflow-hidden rounded-md bg-card">
                <CuoEmpty icon={Activity} text="No activity yet" />
              </div>
            ) : (
              <FestivalDetailSkeleton />
            )
          ) : (
            <>
              {activeTab === "overview" && (
                <div className="flex flex-col gap-3 md:flex-row">
                  <div className="flex min-w-0 flex-col gap-3 md:basis-[60%]">
                    <DetailStatTiles tiles={festivalKpiTiles(data.kpis)} />
                    <FestivalShowsList shows={data.shows} />
                  </div>
                  <div className="flex min-w-0 flex-col gap-3 md:basis-[40%]">
                    <FestivalSalesCard sales={data.sales} shows={data.shows} />
                    <FestivalRecentOrdersCard orders={data.recent_orders} />
                  </div>
                </div>
              )}

              {activeTab === "details" && festival && (
                <FestivalDetailsTab festival={festival} onSaved={refetch} />
              )}

              {activeTab === "activity" && (
                <div className="flex h-[calc(100vh-320px)] flex-col overflow-hidden rounded-md bg-card">
                  <CuoEmpty icon={Activity} text="No activity yet" />
                </div>
              )}

              {activeTab === "orders" && (
                <FestivalGuestsSection shows={data.shows} festivalId={data.festival.id} />
              )}
            </>
          )}
          </div>
        </div>
      </div>
    </PageContainer>
  );
}
